/** @module Auth0DynamicScriptLoader */
/* eslint-disable import/extensions */
import logger from './logging/auth0-logger.mjs';
import Auth0UtilsBrowser from './auth0-utils-browser.mjs';
import { isTenantForEnvironment } from '../config/auth0-tenant-registry.mjs';

const GENERIC_LOADER_ERROR = 'Could not load scripts! The "scripts" arg must be a string url, a script descriptor object ({ url, required true|false, preLoad: func(), postLoad: func(), attributes: { attr1, attr2, ... }}) or an an array of urls or descriptor objects.';


/**
 * @typedef {object} Script
 * @property {string} url The URL for the script to load
 * @property {object} [attributes] An object containing key value pairs for element attributes that the new script element should use. If you need to add a boolean attribute, set the key and value to the same thing (e.g. `{ async: 'async' }`).
 * @property {boolean} [killCache] If the global `killCache` (in the Auth0DynamicScriptLoader.loadScripts) is false, this will allow for selective cache nullification of individual scripts
 * @property {function} [preLoad] A callback function to be run before the script starts load. This will be called before the associated script load starts and NOT before all scripts start loading.
 * @property {function} [postLoad] A callback function to be run when the script is loaded. This will be called after the associated script is loaded and NOT after all scripts are loaded.
 * @property {boolean} [required=true] A boolean indicating if the requested script MUST load. If this is true, then a call to `loadScripts`, upon failure, will throw an Error, otherwise, it will ignore and continue.
 */

class Auth0DynamicScriptLoader {
  constructor(tenantOrOptions) {
    // Support both legacy string tenant param and new options object
    if (typeof tenantOrOptions === 'string') {
      this._tenant = tenantOrOptions;
    } else if (tenantOrOptions && typeof tenantOrOptions === 'object') {
      this._tenant = tenantOrOptions.tenant;
    }
    // If no tenant provided, derive production status from current domain
    this._isProdEnvironment = this._tenant
      ? isTenantForEnvironment(this._tenant, 'prod')
      : Auth0UtilsBrowser.isProdFromCurrentDomain();
  }

  static getScriptCacheKiller = (tenantName) => {
    const isProd = tenantName
      ? isTenantForEnvironment(tenantName, 'prod')
      : Auth0UtilsBrowser.isProdFromCurrentDomain();
    return isProd
      ? Math.floor(Date.now() / 60 / 60 / 60) // prod: invalidate once per minute
      : Math.floor(Date.now()); // non-prod: disable cache
  };

  _getScriptCacheKiller = () => {
    return this._isProdEnvironment
      ? Math.floor(Date.now() / 60 / 60 / 60) // prod: invalidate once per minute
      : Math.floor(Date.now()); // non-prod: disable cache
  };

  _styleAlreadyLoaded = (cssUrl) => {
    return !!document.querySelector(`link[href="${cssUrl}"]`);
  };

  _scriptAlreadyLoaded = (scriptUrl) => {
    return !!document.querySelector(`script[src="${scriptUrl}"]`);
  };

  _hasProp = (obj, prop) => {
    return !!(obj && Object.prototype.hasOwnProperty.call(obj, prop) && obj[prop]);
  };

  _hasUrlProp = (obj) => {
    return this._hasProp(obj, 'url');
  };

  _getAttributesProps = (obj) => {
    return this._hasProp(obj, 'attributes')
      ? Object.keys(obj.attributes)
      : false;
  };

  _buildScriptOrStyleUrl = (scriptObj, killCache) => {
    const url = new URL(scriptObj.url);
    const qs = new URLSearchParams(url.search);

    if (killCache) {
      qs.set('cb', this._getScriptCacheKiller());
    }

    url.search = qs.toString();
    return url.toString();
  };

  _normalizeScriptOrStyleObj = (scriptOrStyleObj) => {
    const baseObj = typeof scriptOrStyleObj === 'string'
      ? { url: scriptOrStyleObj, attributes: {} }
      : scriptOrStyleObj;

    baseObj.required = baseObj.required ?? true;
    return baseObj;
  };

  _normalizeScriptOrStyleArray = (scriptsOrStyles) => {
    return scriptsOrStyles.map(script => this._normalizeScriptOrStyleObj(script));
  };

  _isScriptOrStyleObjectValid = (scriptObj) => {
    return this._hasUrlProp(scriptObj);
  };

  _isScriptOrStyleArrayValid = (scriptsOrStyles) => {
    return Array.isArray(scriptsOrStyles) && this._normalizeScriptOrStyleArray(scriptsOrStyles).every(script => this._isScriptOrStyleObjectValid(script));
  };

  _loadScript = async (scriptObj, killCache = false) => {
    return new Promise((resolve, reject) => {
      const loadErrorHandler = (err) => {
        if (scriptObj.required) {
          const errMsg = `Failed to load REQUIRED script at "${scriptObj.url}". ${JSON.stringify(err)}`;
          logger.error(errMsg);
          reject(new Error(errMsg));
        } else {
          logger.warn(`Failed to load optional script at "${scriptObj.url}". ${JSON.stringify(err)}`);
          resolve();
        }
      };

      try {
        killCache = killCache || scriptObj.killCache;
        const scriptUrl = this._buildScriptOrStyleUrl(scriptObj, killCache);
        console.log(`loading script @ ${scriptUrl}`);

        const dynamicScript = document.createElement('script');
        dynamicScript.setAttribute('src', scriptUrl);

        const attrs = this._getAttributesProps(scriptObj) || [];
        attrs.forEach((attr) => {
          dynamicScript.setAttribute(attr, scriptObj.attributes[attr]);
        });

        const scriptLoaded = () => {
          if (scriptObj.postLoad) {
            console.log(`script @ ${scriptUrl} POST load`);
            scriptObj.postLoad.call();
          }
          console.log(`loading script @ ${scriptUrl} FINISHED`);
          resolve();
        };

        dynamicScript.onload = scriptLoaded;

        dynamicScript.onerror = (err) => {
          loadErrorHandler(err);
        };

        if (scriptObj.preLoad) {
          console.log(`script @ ${scriptUrl} PRE load`);
          scriptObj.preLoad.call();
        }

        if (this._scriptAlreadyLoaded(scriptUrl)) {
          console.log(`script @ ${scriptUrl} is already loaded!`);
          scriptLoaded();
        } else {
          document.head.appendChild(dynamicScript);
        }
      } catch (error) {
        loadErrorHandler(error);
      }
    });
  };

  _loadStyle = async (styleObj, killCache = false) => {
    return new Promise((resolve, reject) => {
      const loadErrorHandler = (err) => {
        if (styleObj.required) {
          const errMsg = `Failed to load REQUIRED style at "${styleObj.url}". ${JSON.stringify(err)}`;
          logger.error(errMsg);
          reject(new Error(errMsg));
        } else {
          logger.warn(`Failed to load optional style at "${styleObj.url}". ${JSON.stringify(err)}`);
          resolve();
        }
      };

      try {
        killCache = killCache || styleObj.killCache;
        const styleUrl = this._buildScriptOrStyleUrl(styleObj, killCache);
        console.log(`loading style @ ${styleUrl}`);

        const dynamicStyle = document.createElement('link');
        dynamicStyle.setAttribute('href', styleUrl);
        dynamicStyle.setAttribute('rel', 'stylesheet');
        dynamicStyle.setAttribute('type', 'text/css');

        const styleLoaded = () => {
          console.log(`loading style @ ${styleUrl} FINISHED`);
          resolve();
        };
        dynamicStyle.onload = styleLoaded;

        dynamicStyle.onerror = (err) => {
          loadErrorHandler(err);
        };

        if (this._styleAlreadyLoaded(styleUrl)) {
          styleLoaded();
        } else {
          document.head.appendChild(dynamicStyle);
        }
      } catch (error) {
        loadErrorHandler(error);
      }
    });
  };

  /**
   * @description This function will load a single or multiple HTML <script> element(s) dynamically. It returns a promise when all required scripts are loaded
   * @param {(string|Script[])} scripts Either a script url or an array of scripts with shape as defined below
   * @param {boolean} [loadSynchronously=false] A boolean indicating whether all scripts in the list should load async or sync.
   * @param {boolean} [killCache=false] A boolean indicating whether the dynamic script loader should append a random url search param in order to avoid CDN caches.
   * @returns {Promise} Returns a promise indicating is all required scripts were successfully loaded.
   */
  loadScripts = (scripts, loadSynchronously = false, killCache = false) => {
    if (typeof scripts === 'string') {
      scripts = [this._normalizeScriptOrStyleObj(scripts)];
    }
    if (typeof scripts === 'object' && this._hasUrlProp(scripts)) {
      scripts = [scripts];
    }
    scripts = this._normalizeScriptOrStyleArray(scripts);
    if (!this._isScriptOrStyleArrayValid(scripts)) {
      throw new Error(GENERIC_LOADER_ERROR);
    }
    console.log(`start loadScripts for ${scripts.length} script(s)...`);
    if (loadSynchronously) {
      return new Promise(async (resolve, reject) => {
        for (let i = 0; i < scripts.length; i++) {
          const scriptObj = scripts[i];
          try {
            // eslint-disable-next-line no-await-in-loop
            await this._loadScript(scriptObj, killCache);
          } catch (error) {
            // will only throw if script was required
            reject(error);
          }
        }
        resolve(true);
      });
    }

    // otherwise an async free for all
    const loadPromises = scripts.map((resKey) => {
      return this._loadScript(resKey, killCache);
    });
    return Promise.all(loadPromises);
  };

  /**
   * @description This function will load a single or multiple HTML <style> element(s) dynamically. It returns a promise when all required styles are loaded
   * @param {string|Array} styles Either a style url or an array of styles with shape as defined below
   * @param {string} styles.url The URL for the style to load
   * @param {object} [styles.attributes] An object containing key value pairs for element attributes that the new style element should use. If you need to add a boolean attribute, set the key and value to the same thing (e.g. `{ async: 'async' }`).
   * @param {boolean} [scripts.killCache] If the global `killCache` (see below) is false, this will allow for selective cache nullification of individual styles
   * @param {function} [styles.preLoad] A callback function to be run before the style starts load. This will be called before the associated style load starts and NOT before all styles start loading.
   * @param {function} [styles.postLoad] A callback function to be run when the style is loaded. This will be called after the associated style is loaded and NOT after all styles are loaded.
   * @param {boolean} [styles.required=true] A boolean indicating if the requested style MUST load. If this is true, then a call to `loadStyles`, upon failure, will throw an Error, otherwise, it will ignore and continue.
   * @param {boolean} [killCache=false] A boolean indicating whether the dynamic style loader should append a random url search param in order to avoid CDN caches.
   * @returns {Promise} Returns a promise indicating is all required styles were successfully loaded.
   */
  loadStyles = (styles, killCache = false) => {
    if (typeof styles === 'string') {
      styles = [this._normalizeScriptOrStyleObj(styles)];
    }
    if (typeof styles === 'object' && this._hasUrlProp(styles)) {
      styles = [styles];
    }
    styles = this._normalizeScriptOrStyleArray(styles);
    if (!this._isScriptOrStyleArrayValid(styles)) {
      throw new Error(GENERIC_LOADER_ERROR);
    }
    return new Promise(async (resolve, reject) => {
      for (let i = 0; i < styles.length; i++) {
        const styleObj = styles[i];
        try {
          // eslint-disable-next-line no-await-in-loop
          await this._loadStyle(styleObj, killCache);
        } catch (error) {
          // will only throw if style was required
          reject(error);
        }
      }
      resolve(true);
    });
  };
}

export default Auth0DynamicScriptLoader;
